import { ChevronDown, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { JiraTransition } from '@/types';

export interface AddTransitionDropdownProps {
  availableTransitions: JiraTransition[];
  onAdd: (transitionId: string) => void;
  disabled?: boolean;
}

export const AddTransitionDropdown = ({
  availableTransitions,
  onAdd,
  disabled = false,
}: AddTransitionDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4, // 4px gap
        left: rect.left,
        width: rect.width,
      });
    } else {
      setPosition(null);
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleAdd = (transitionId: string) => {
    onAdd(transitionId);
    setIsOpen(false);
  };

  if (availableTransitions.length === 0) {
    return (
      <button
        type="button"
        disabled
        className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-disabled flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm"
      >
        <Plus className="h-4 w-4" />
        No more transitions available
      </button>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:bg-chariot-background-secondary disabled:hover:bg-chariot-background-primary flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
        Add Transition
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && position && (
        <>
          {/* Backdrop to close on click outside */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown menu with fixed positioning */}
          <div
            ref={dropdownRef}
            className="border-chariot-stroke bg-chariot-background-primary fixed z-50 overflow-hidden rounded-lg border shadow-lg"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
            }}
          >
            <div className="max-h-60 overflow-y-auto">
              {availableTransitions.map(transition => (
                <button
                  key={transition.id}
                  type="button"
                  onClick={() => handleAdd(transition.id)}
                  className="text-chariot-text-primary hover:bg-chariot-background-secondary flex w-full flex-col gap-0.5 px-4 py-2 text-left transition-colors"
                >
                  <span className="font-medium">{transition.name}</span>
                  {transition.to && (
                    <span className="text-chariot-text-secondary text-xs">
                      → {transition.to.name}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
